import React from 'react';
import { QrCode, Building2, User, Star, Zap, Globe, Users, Crown } from 'lucide-react';

const BadgePreview = React.memo(({ layout = 'standard', showQR = true, showLogo = true, data = {}, logoConfig = {} }) => {
  const {
    name = 'John Doe',
    company = 'Tech Corp',
    title = 'Software Engineer',
    eventName = 'Tech Conference 2025',
    eventLogo = null,
    registrationNumber = null,
    profession = null,
  } = data;

  const {
    logo_url = eventLogo,
    logo_position = 'top-center',
    logo_shape = 'circular',
    logo_size = 60,
    show_registration_number = false,
    show_attendee_title = true,
    show_company_name = true,
  } = logoConfig;


  // Layout configurations with cutting-edge 2024-2025 design trends
  const layouts = {
    standard: {
      bg: 'bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700',
      accent: 'bg-gradient-to-r from-blue-500/90 to-indigo-600/90',
      textColor: 'text-white',
      secondaryColor: 'text-blue-100',
      icon: Building2,
      pattern: 'opacity-10',
      glassEffect: 'backdrop-blur-md bg-white/10',
      shadowColor: 'shadow-blue-500/25',
    },
    modern: {
      bg: 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-600',
      accent: 'bg-gradient-to-r from-purple-500/80 to-pink-500/80',
      textColor: 'text-white',
      secondaryColor: 'text-purple-100',
      icon: Zap,
      pattern: 'opacity-20',
      glassEffect: 'backdrop-blur-lg bg-white/15',
      shadowColor: 'shadow-purple-500/30',
    },
    minimal: {
      bg: 'bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200',
      accent: 'bg-gradient-to-r from-gray-700/90 to-slate-800/90',
      textColor: 'text-gray-900',
      secondaryColor: 'text-gray-600',
      icon: User,
      pattern: 'opacity-5',
      glassEffect: 'backdrop-blur-sm bg-white/60',
      shadowColor: 'shadow-gray-300/40',
    },
    corporate: {
      bg: 'bg-gradient-to-br from-slate-700 via-gray-800 to-zinc-900',
      accent: 'bg-gradient-to-r from-slate-600/85 to-gray-700/85',
      textColor: 'text-white',
      secondaryColor: 'text-slate-300',
      icon: Building2,
      pattern: 'opacity-12',
      glassEffect: 'backdrop-blur-md bg-white/8',
      shadowColor: 'shadow-slate-600/25',
    },
    executive: {
      bg: 'bg-gradient-to-br from-emerald-700 via-teal-700 to-cyan-800',
      accent: 'bg-gradient-to-r from-emerald-500/85 to-teal-600/85',
      textColor: 'text-white',
      secondaryColor: 'text-emerald-100',
      icon: Crown,
      pattern: 'opacity-18',
      glassEffect: 'backdrop-blur-lg bg-white/12',
      shadowColor: 'shadow-emerald-500/30',
    },
    creative: {
      bg: 'bg-gradient-to-br from-orange-400 via-red-500 to-pink-600',
      accent: 'bg-gradient-to-r from-yellow-400/80 to-orange-500/80',
      textColor: 'text-white',
      secondaryColor: 'text-orange-100',
      icon: Star,
      pattern: 'opacity-25',
      glassEffect: 'backdrop-blur-xl bg-white/20',
      shadowColor: 'shadow-orange-500/35',
    },
    tech: {
      bg: 'bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-700',
      accent: 'bg-gradient-to-r from-cyan-400/75 to-blue-500/75',
      textColor: 'text-white',
      secondaryColor: 'text-cyan-100',
      icon: Zap,
      pattern: 'opacity-30',
      glassEffect: 'backdrop-blur-2xl bg-white/25',
      shadowColor: 'shadow-cyan-400/40',
    },
    conference: {
      bg: 'bg-gradient-to-br from-violet-500 via-purple-600 to-blue-700',
      accent: 'bg-gradient-to-r from-violet-500/80 to-purple-600/80',
      textColor: 'text-white',
      secondaryColor: 'text-violet-100',
      icon: Globe,
      pattern: 'opacity-18',
      glassEffect: 'backdrop-blur-lg bg-white/15',
      shadowColor: 'shadow-violet-500/30',
    },
    networking: {
      bg: 'bg-gradient-to-br from-rose-400 via-pink-500 to-fuchsia-700',
      accent: 'bg-gradient-to-r from-rose-400/75 to-pink-500/75',
      textColor: 'text-white',
      secondaryColor: 'text-rose-100',
      icon: Users,
      pattern: 'opacity-22',
      glassEffect: 'backdrop-blur-xl bg-white/18',
      shadowColor: 'shadow-rose-400/35',
    },
    premium: {
      bg: 'bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-600',
      accent: 'bg-gradient-to-r from-amber-400/80 to-yellow-500/80',
      textColor: 'text-white',
      secondaryColor: 'text-amber-100',
      icon: Crown,
      pattern: 'opacity-28',
      glassEffect: 'backdrop-blur-2xl bg-white/22',
      shadowColor: 'shadow-amber-400/40',
    },
  };

  const currentLayout = layouts[layout] || layouts.standard;
  const IconComponent = currentLayout.icon;

  return (
    <div className="w-full h-full flex items-center justify-center p-4">
      {/* Badge Card - Portrait orientation with modern shadow */}
      <div className={`relative w-64 h-96 rounded-2xl shadow-2xl ${currentLayout.shadowColor} overflow-hidden ${currentLayout.bg}`}>
        {/* Animated Background Pattern */}
        <div className={`absolute inset-0 ${currentLayout.pattern}`}>
          <div className="absolute top-4 right-4 animate-pulse">
            <IconComponent className="h-32 w-32 text-white/8" />
          </div>
          {/* Additional decorative elements */}
          <div className="absolute bottom-8 left-4 opacity-5">
            <IconComponent className="h-16 w-16 text-white rotate-45" />
          </div>
        </div>

        {/* Enhanced Header Section with Logo Positioning */}
        <div className={`relative ${currentLayout.accent} ${currentLayout.glassEffect} p-4 border-b border-white/20`}>
          <div className="flex items-center justify-between h-12">
            {/* Logo positioning logic */}
            {showLogo && logo_position === 'top-left' && (
              <div className="flex-shrink-0">
                {logo_url ? (
                  <img 
                    src={logo_url} 
                    alt="Event Logo" 
                    className={`shadow-lg ${logo_shape === 'circular' ? 'rounded-full' : 'rounded-lg'}`}
                    style={{ width: `${logo_size}px`, height: `${logo_size}px`, objectFit: 'cover' }}
                  />
                ) : (
                  <div 
                    className={`${currentLayout.glassEffect} ${logo_shape === 'circular' ? 'rounded-full' : 'rounded-lg'} flex items-center justify-center border border-white/30 shadow-lg`}
                    style={{ width: `${logo_size}px`, height: `${logo_size}px` }}
                  >
                    <IconComponent className="h-6 w-6 text-white drop-shadow-sm" />
                  </div>
                )}
              </div>
            )}

            {/* Event Name - Center or adjust based on logo position */}
            <div className={`flex-1 ${logo_position === 'top-center' ? 'text-center' : logo_position === 'top-left' ? 'text-center' : 'text-center'}`}>
              {showLogo && logo_position === 'top-center' && (
                <div className="mb-2">
                  {logo_url ? (
                    <img 
                      src={logo_url} 
                      alt="Event Logo" 
                      className={`mx-auto shadow-lg ${logo_shape === 'circular' ? 'rounded-full' : 'rounded-lg'}`}
                      style={{ width: `${logo_size}px`, height: `${logo_size}px`, objectFit: 'cover' }}
                    />
                  ) : (
                    <div 
                      className={`mx-auto ${currentLayout.glassEffect} ${logo_shape === 'circular' ? 'rounded-full' : 'rounded-lg'} flex items-center justify-center border border-white/30 shadow-lg`}
                      style={{ width: `${logo_size}px`, height: `${logo_size}px` }}
                    >
                      <IconComponent className="h-6 w-6 text-white drop-shadow-sm" />
                    </div>
                  )}
                </div>
              )}
              <h3 className={`text-xs font-bold tracking-widest uppercase ${currentLayout.textColor} drop-shadow-sm`}>
                {eventName}
              </h3>
            </div>

            {/* Right positioned logo */}
            {showLogo && logo_position === 'top-right' && (
              <div className="flex-shrink-0">
                {logo_url ? (
                  <img 
                    src={logo_url} 
                    alt="Event Logo" 
                    className={`shadow-lg ${logo_shape === 'circular' ? 'rounded-full' : 'rounded-lg'}`}
                    style={{ width: `${logo_size}px`, height: `${logo_size}px`, objectFit: 'cover' }}
                  />
                ) : (
                  <div 
                    className={`${currentLayout.glassEffect} ${logo_shape === 'circular' ? 'rounded-full' : 'rounded-lg'} flex items-center justify-center border border-white/30 shadow-lg`}
                    style={{ width: `${logo_size}px`, height: `${logo_size}px` }}
                  >
                    <IconComponent className="h-6 w-6 text-white drop-shadow-sm" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main Content with Enhanced Layout */}
        <div className="relative p-4 flex flex-col items-center justify-center flex-1 space-y-3">
          {/* Enhanced Avatar with Glassmorphism */}
          <div className={`w-16 h-16 rounded-full ${currentLayout.glassEffect} flex items-center justify-center border-2 border-white/40 shadow-xl ring-2 ring-white/20`}>
            <User className={`h-8 w-8 ${currentLayout.textColor} opacity-90 drop-shadow-sm`} />
          </div>

          {/* Attendee Title (grayed, above name) */}
          {show_attendee_title && title && (
            <p className={`text-xs text-center ${currentLayout.secondaryColor} font-medium opacity-75 drop-shadow-sm tracking-wide uppercase`}>
              {title}
            </p>
          )}

          {/* Name with Enhanced Typography */}
          <h2 className={`text-lg font-bold text-center ${currentLayout.textColor} leading-tight drop-shadow-lg tracking-wide`}>
            {name}
          </h2>

          {/* Profession/Position */}
          {profession && (
            <div className={`${currentLayout.glassEffect} px-3 py-1 rounded-full border border-white/30 shadow-lg`}>
              <p className={`text-xs text-center font-semibold ${currentLayout.secondaryColor} drop-shadow-sm tracking-wide`}>
                {profession}
              </p>
            </div>
          )}

          {/* Company/Organization */}
          {show_company_name && company && (
            <p className={`text-xs text-center ${currentLayout.secondaryColor} font-medium tracking-wider uppercase opacity-90 drop-shadow-sm`}>
              {company}
            </p>
          )}

          {/* Bottom section with QR and Registration Number */}
          <div className="flex flex-col items-center space-y-2 mt-4">
            {/* Enhanced QR Code with Glassmorphism */}
            {showQR && (
              <div className={`${currentLayout.glassEffect} p-3 rounded-2xl shadow-xl border border-white/30 ring-1 ring-white/20`}>
                <div className="w-12 h-12 flex items-center justify-center bg-white/95 rounded-lg shadow-inner">
                  <QrCode className="h-10 w-10 text-gray-800" />
                </div>
              </div>
            )}

            {/* Registration Number */}
            {show_registration_number && registrationNumber && (
              <div className={`${currentLayout.glassEffect} px-3 py-1 rounded-full border border-white/30 shadow-lg`}>
                <p className={`text-xs text-center font-mono ${currentLayout.secondaryColor} drop-shadow-sm tracking-wider`}>
                  {registrationNumber}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Footer with Gradient Overlay */}
        <div className={`relative h-4 ${currentLayout.accent} ${currentLayout.glassEffect}`}>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
        </div>

        {/* Subtle Border Highlight */}
        <div className="absolute inset-0 rounded-2xl border border-white/20 pointer-events-none"></div>
      </div>
    </div>
  );
});

export default BadgePreview;
